<template>
  <div class="mb-3">
    <label>
      <question-display :question="question" customClass="header-checkbox" />
    </label>

    <div v-if="question['selection-list']">
      <div v-if="question['selection-list'].category">
        <div v-for="(options, category) in categorizedOptions" :key="category" class="mb-4">
          <h5 v-if="category">{{ category }}</h5>
          <div class="checkbox" :class="{ 'two-column-layout': question.id === '12.b' }">
            <div v-for="option in options" :key="option.value" class="checkWidth">
              <!-- Check if this option has sub-options for Q16 -->
              <div v-if="question.id === '16' && hasSubOptionsInCategory(option, options)" class="option-group has-sub-options">
                <label class="curved-checkbox main-option">
                  <input
                    type="checkbox"
                    :value="option.value"
                    :checked="isChecked(option.value)"
                    @change="updateCheckbox($event, option.value, option.name)"
                    class="form-check-input"
                    :disabled="isDisabledOptionsFrom7A(option)"
                  />
                  {{ option.name }}
                </label>
                
                <!-- Sub-options for Q16 - same background, no gap -->
                <div class="sub-options-list">
                  <label v-for="subOption in getSubOptionsInCategory(option, options)" :key="subOption.value" class="curved-checkbox sub-option">
                    <input
                      type="checkbox"
                      :value="subOption.value"
                      :checked="isChecked(subOption.value)"
                      @change="updateCheckbox($event, subOption.value, subOption.name)"
                      class="form-check-input"
                    />
                    {{ subOption.name }}
                  </label>
                </div>
              </div>
              
              <!-- Regular option (not a parent or sub-option) -->
              <label v-else-if="!isSubOptionInCategory(option, options)" class="curved-checkbox">
                <input
                  type="checkbox"
                  :value="option.value"
                  :checked="isChecked(option.value)"
                  @change="updateCheckbox($event, option.value, option.name)"
                  class="form-check-input"
                  :disabled="isDisabledOptionsFrom7A(option)"
                />
                {{ option.name }}
              </label>
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <div class="checkbox" :class="{ 'two-column-layout': question.id === '12.b' }">
          <div v-for="option in nonCategoryOptions" :key="option.value" class="checkWidth">
            <!-- Main option with sub-options grouped together -->
            <div class="option-group" :class="{ 'has-sub-options': question.id === '16' && hasSubOptions(option) }">
              <label class="curved-checkbox main-option">
                <input
                  type="checkbox"
                  :value="option.value"
                  :checked="isChecked(option.value)"
                  @change="updateCheckbox($event, option.value, option.name)"
                  class="form-check-input"
                />
                {{ option.name }}
              </label>
              
              <!-- Sub-options for Q16 - same background, no gap -->
              <div v-if="question.id === '16' && hasSubOptions(option)" class="sub-options-list">
                <label v-for="subOption in getSubOptions(option)" :key="subOption.value" class="curved-checkbox sub-option">
                  <input
                    type="checkbox"
                    :value="subOption.value"
                    :checked="isChecked(subOption.value)"
                    @change="updateCheckbox($event, subOption.value, subOption.name)"
                    class="form-check-input"
                  />
                  {{ subOption.name }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="checkbox">
        <div v-for="option in question.options" :key="option" class="curved-checkbox">
          <label class="form-check-label">
            <input
              type="checkbox"
              :value="option"
              :checked="isChecked(option)"
              @change="updateCheckbox($event, option, option)"
              class="form-check-input"
            />
            {{ option }}
          </label>
        </div>
      </div>
    </div>

    <div v-if="isOtherSelected" class="mt-3">
      <input
        type="text"
        v-model="otherSpecify"
        placeholder="Please specify"
        class="form-control"
        @input="updateOtherSpecify"
      />
    </div>

    <small
      v-if="showValidation && question.required && !inputValue.length"
      class="text-danger"
    >
      This field is required.
    </small>
  </div>
</template>

<script>
import dropdownValues from "@/assets/data/dropdown-values/dropdown.json";
import surveyService from "@/services/surveyService";
import QuestionDisplay from "../utils/QuestionDisplay.vue";

export default {
  components: {
    QuestionDisplay,
  },
  props: {
    question: Object,
    required: Boolean,
    showValidation: Boolean,
  },
  data() {
    return {
      inputValue: surveyService.getAnswer(this.question.id) || [],
      otherSpecify: "",
    };
  },
  computed: {
    categorizedOptions() {
      const categories = {};
      let options = this.question.options || [];

      if (this.question["selection-list"]?.category) {
        const key = this.question["selection-list"].value;
        options = dropdownValues[key] || [];
      }

      options.forEach((option) => {
        if (!categories[option.category]) {
          categories[option.category] = [];
        }
        categories[option.category].push(option);
      });

      // Special handling for Q16 to group both sub-option pairs together
      if (this.question.id === '16' && categories[""]) {
        const emptyCategory = categories[""];
        
        // Move "Visit historical locations" and its sub-option to right column
        const historicalIndex = emptyCategory.findIndex(opt => opt.value === '14');
        const america250Index = emptyCategory.findIndex(opt => opt.value === '24');
        
        if (historicalIndex > -1 && america250Index > -1) {
          // Remove both options from their current positions
          const historical = emptyCategory.splice(historicalIndex, 1)[0];
          const america250 = emptyCategory.splice(america250Index > historicalIndex ? america250Index - 1 : america250Index, 1)[0];
          
          // Insert them after "Visit small towns/Countryside" (value: "13")
          const smallTownsIndex = emptyCategory.findIndex(opt => opt.value === '13');
          if (smallTownsIndex > -1) {
            emptyCategory.splice(smallTownsIndex + 1, 0, historical, america250);
          }
        }
        
        // Move "Attend a sporting event" and its sub-option right after the historical group
        const sportingEventIndex = emptyCategory.findIndex(opt => opt.value === '6');
        const fifaIndex = emptyCategory.findIndex(opt => opt.value === '23');
        
        if (sportingEventIndex > -1 && fifaIndex > -1) {
          // Remove both options from their current positions
          const sportingEvent = emptyCategory.splice(sportingEventIndex, 1)[0];
          const fifa = emptyCategory.splice(fifaIndex > sportingEventIndex ? fifaIndex - 1 : fifaIndex, 1)[0];
          
          // Insert them right after "America 250 event" to group both sub-option pairs together
          const america250NewIndex = emptyCategory.findIndex(opt => opt.value === '24');
          if (america250NewIndex > -1) {
            emptyCategory.splice(america250NewIndex + 1, 0, sportingEvent, fifa);
          }
        }
      }

      return categories;
    },

    nonCategoryOptions() {
      let options = this.question.options || [];
      if (this.question["selection-list"] && !this.question["selection-list"].category) {
        const key = this.question["selection-list"].value;
        options = dropdownValues[key] || [];
      }
      
      // For Q16, group sub-options with their parent options
      if (this.question.id === '16') {
        return this.groupQ16Options(options);
      }
      
      return options;
    },

    isOtherSelected() {
      // Q16 "Other (specify)" has value "22"
      if (this.question.id === '16') {
        return this.inputValue.some((item) => item.key === "22" || item.key === "Other (specify)");
      }
      const result = this.inputValue.some((item) => item.key === "14" || item.key === "Other (Specify)");
      console.log('Q7b isOtherSelected:', { inputValue: this.inputValue, result });
      return result;
    },

    unmatchedOptions() {
      const allOptions = [
        ...this.nonCategoryOptions,
        ...Object.values(this.categorizedOptions).flat(),
      ].map((option) => option.value);

      return this.inputValue.filter((item) => !allOptions.includes(item.key));
    },
    


  },
  methods: {
    isChecked(optionValue) {
      return this.inputValue.some((item) => item.key === optionValue);
    },

    updateCheckbox(event, optionValue, optionName) {
      const updated = [...this.inputValue];

      if (event.target.checked) {
        if (!updated.some((item) => item.key === optionValue)) {
          updated.push({ key: optionValue, value: optionName, others: null });
        }
        
        // Q16 Auto-selection logic
        if (this.question.id === '16') {
          // If 6a (FIFA World Cup) is selected, auto-select option 6 (Attend a sporting event)
          if (optionValue === '23' && !updated.some((item) => item.key === '6')) {
            updated.push({ key: '6', value: 'Attend a sporting event', others: null });
          }
          // If 14a (America 250) is selected, auto-select option 14 (Visit historical locations)
          if (optionValue === '24' && !updated.some((item) => item.key === '14')) {
            updated.push({ key: '14', value: 'Visit historical locations', others: null });
          }
        }
      } else {
        const index = updated.findIndex((item) => item.key === optionValue);
        if (index > -1) updated.splice(index, 1);
        
        // Q16 Auto-deselection logic - if parent option is deselected, also deselect sub-options
        if (this.question.id === '16') {
          // If option 6 (Attend a sporting event) is deselected, also deselect 6a (FIFA World Cup)
          if (optionValue === '6') {
            const fifa6aIndex = updated.findIndex((item) => item.key === '23');
            if (fifa6aIndex > -1) updated.splice(fifa6aIndex, 1);
          }
          // If option 14 (Visit historical locations) is deselected, also deselect 14a (America 250)
          if (optionValue === '14') {
            const america250Index = updated.findIndex((item) => item.key === '24');
            if (america250Index > -1) updated.splice(america250Index, 1);
          }
        }
      }

      this.inputValue = updated;
      surveyService.setAnswer(this.question.id, updated);
      this.$emit("input", updated);
      
      // Force reactivity update
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    updateOtherSpecify(event) {
      this.otherSpecify = event.target.value;

      const otherKey = this.question.id === '16' ? "22" : "14";
      const otherItem = this.inputValue.find(
        (item) => item.key === otherKey || item.key === "Other (Specify)" || item.key === "Other (specify)"
      );

      if (otherItem) {
        otherItem.others = this.otherSpecify;
        surveyService.setAnswer(this.question.id, this.inputValue);
        this.$emit("input", this.inputValue);
      }
    },

    updateInputOptions() {
      // force re-compute
      this.categorizedOptions;
      this.nonCategoryOptions;
    },

    loadSavedOtherSpecify() {
      const otherKey = this.question.id === '16' ? "22" : "14";
      const savedOther = this.inputValue.find(
        (item) => item.key === otherKey || item.key === "Other (Specify)" || item.key === "Other (specify)"
      );
      this.otherSpecify = savedOther?.others || "";
    },
    isDisabledOptionsFrom7A(option) {
      if (this.question.id === '7b') {
        const selectedFrom7A = surveyService.getAnswer('7a')?.key;
        if (option.value === selectedFrom7A && selectedFrom7A !== 'Other (Specify)') {
          return true;
        }
      }
      return false;
    },

    hasSubOptionsInCategory(option, allOptions) {
      // Check if this main option has sub-options in the same category
      if (this.question.id === '16') {
        if (option.value === '6') {
          // Check if FIFA World Cup option exists in the same category
          return allOptions.some(opt => opt.value === '23');
        } else if (option.value === '14') {
          // Check if America 250 option exists in the same category
          return allOptions.some(opt => opt.value === '24');
        }
      }
      return false;
    },

    getSubOptionsInCategory(option, allOptions) {
      // Get sub-options for a main option within the same category
      if (this.question.id === '16') {
        if (option.value === '6') {
          // Return FIFA World Cup option with cleaned name
          const subOption = allOptions.find(opt => opt.value === '23');
          if (subOption) {
            return [{
              ...subOption,
              name: "FIFA World Cup game or fan event" // Remove "6a" prefix
            }];
          }
        } else if (option.value === '14') {
          // Return America 250 option with cleaned name
          const subOption = allOptions.find(opt => opt.value === '24');
          if (subOption) {
            return [{
              ...subOption,
              name: "America 250 event, celebration, or road trip" // Remove "14a" prefix
            }];
          }
        }
      }
      return [];
    },

    isSubOptionInCategory(option, allOptions) {
      // Check if this is a sub-option that should be hidden from main list
      if (this.question.id === '16') {
        return option.value === '23' || option.value === '24';
      }
      return false;
    },

    groupQ16Options(options) {
      // For Q16, we need to separate main options from sub-options
      // Sub-options will be rendered nested under their parent options
      const mainOptions = [];
      
      options.forEach(option => {
        // Skip sub-options in the main list - they'll be rendered as nested items
        if (option.value !== '23' && option.value !== '24') {
          mainOptions.push(option);
        }
      });
      
      return mainOptions;
    },

    hasSubOptions(option) {
      // Check if this main option has sub-options
      if (this.question.id === '16') {
        return option.value === '6' || option.value === '14';
      }
      return false;
    },

    getSubOptions(option) {
      // Get sub-options for a main option
      if (this.question.id === '16') {
        const allOptions = this.question.options || [];
        if (this.question["selection-list"] && !this.question["selection-list"].category) {
          const key = this.question["selection-list"].value;
          const dropdownOptions = dropdownValues[key] || [];
          
          if (option.value === '6') {
            // Return 6a (FIFA World Cup) for option 6, but clean the name
            const subOption = dropdownOptions.find(opt => opt.value === '23');
            if (subOption) {
              return [{
                ...subOption,
                name: "FIFA World Cup game or fan event" // Remove "6a" prefix
              }];
            }
          } else if (option.value === '14') {
            // Return 14a (America 250) for option 14, but clean the name
            const subOption = dropdownOptions.find(opt => opt.value === '24');
            if (subOption) {
              return [{
                ...subOption,
                name: "America 250 event, celebration, or road trip" // Remove "14a" prefix
              }];
            }
          }
        }
      }
      return [];
    },
  },
  watch: {
    question(newQ) {
      this.inputValue = surveyService.getAnswer(newQ.id) || [];
      this.loadSavedOtherSpecify();
      this.updateInputOptions();
    },
    inputValue(newVal) {
      const unmatched = this.unmatchedOptions;

      if (unmatched.length > 0) {
        this.inputValue = this.inputValue.filter(
          (item) => !unmatched.some((un) => un.key === item.key)
        );
        this.otherSpecify = unmatched.map((item) => item.key).join(", ");
        surveyService.setAnswer(this.question.id, this.inputValue);
        console.log(newVal);
      }
    },
  },
  mounted() {
    if (this.question["selection-list"]) {
      this.updateInputOptions();
    }
    this.loadSavedOtherSpecify();
  },
};
</script>

<style scoped>
.two-column-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  align-items: start;
}

.two-column-layout .checkWidth {
  margin-bottom: 8px;
}

@media (max-width: 768px) {
  .two-column-layout {
    grid-template-columns: 1fr;
  }
}

/* Sub-option styling for Q16 */
.option-group {
  margin-bottom: 7px;
  width: 100%;
}

.option-group.has-sub-options .main-option {
  margin-bottom: 0 !important;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: none;
}

.sub-options-list {
  margin: 0;
  padding: 0;
}

.sub-options-list .sub-option {
  margin-bottom: 0 !important;
  margin-top: 0 !important;
  border-top: 1px solid #E4D9F8;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  padding-left: 30px;
  font-style: italic;
  color: #666;
  font-size: 13px;
  background-color: #F2F4F7;
}

.sub-options-list .sub-option:last-child {
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

.sub-options-list .sub-option input[type="checkbox"] {
  transform: scale(0.9);
}

/* Ensure two-column layout still works with grouped options */
.checkbox {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  align-items: start;
}

.checkbox .checkWidth {
  width: 100%;
  margin-bottom: 0;
}

/* Override for specific questions that need different layout */
.checkbox.two-column-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

/* Fix for mobile responsiveness */
@media (max-width: 768px) {
  .checkbox {
    display: block;
  }
  
  .checkbox .checkWidth {
    width: 100% !important;
    margin-bottom: 7px;
  }
  
  .sub-options-list {
    flex-direction: column;
    gap: 3px;
  }
  
  .sub-options-list .sub-option {
    flex: 1 1 auto;
  }
}
</style>
